/**
 * ConstraintWarning - Shows layout imbalance warning with quick fixes
 */

import { AlertTriangle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import type { SuggestedFix } from '@/lib/templates/single-row/calculations'

interface ConstraintWarningProps {
  difference: number
  buildingWidth: number
  suggestedFixes: SuggestedFix[]
  onApplyFix: (fix: SuggestedFix) => void
  className?: string
}

export function ConstraintWarning({
  difference,
  buildingWidth,
  suggestedFixes,
  onApplyFix,
  className,
}: ConstraintWarningProps) {
  const isOver = difference > 0
  const totalUsed = buildingWidth + difference

  return (
    <div
      className={cn(
        'rounded-md border p-3 space-y-2',
        isOver
          ? 'bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800'
          : 'bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800',
        className
      )}
    >
      <div className="flex items-start gap-2">
        <AlertTriangle
          className={cn(
            'h-4 w-4 mt-0.5 shrink-0',
            isOver ? 'text-amber-600 dark:text-amber-400' : 'text-blue-600 dark:text-blue-400'
          )}
        />
        <div className="space-y-1 text-sm">
          <p className={cn('font-medium', isOver ? 'text-amber-800 dark:text-amber-200' : 'text-blue-800 dark:text-blue-200')}>
            Layout Imbalance
          </p>
          <div className="text-xs space-y-0.5 text-muted-foreground">
            <div className="flex justify-between gap-4">
              <span>Building width:</span>
              <span className="font-mono">{buildingWidth.toFixed(0)} ft</span>
            </div>
            <div className="flex justify-between gap-4">
              <span>Units + walls:</span>
              <span className="font-mono">{totalUsed.toFixed(1)} ft</span>
            </div>
            <div className={cn('flex justify-between gap-4 font-medium', isOver ? 'text-amber-700 dark:text-amber-300' : 'text-blue-700 dark:text-blue-300')}>
              <span>Difference:</span>
              <span className="font-mono">
                {isOver ? '+' : ''}{difference.toFixed(1)} ft {isOver ? 'over' : 'under'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {suggestedFixes.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-xs text-muted-foreground">Quick fixes:</p>
          <div className="flex flex-wrap gap-1.5">
            {suggestedFixes.map((fix, index) => (
              <Button
                key={index}
                type="button"
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                onClick={() => onApplyFix(fix)}
                title={fix.description}
              >
                {fix.label}
              </Button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
